import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { 
    Beaker, Plus, Loader2, User, Calendar, ChevronDown, ChevronUp,
    CheckCircle, AlertTriangle, XCircle, Search, Eye, Trash2
} from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { toast } from 'sonner';

// SpinTouch parameters configuration
const PARAMETER_GROUPS = [
    {
        name: "Désinfection",
        params: ["ph", "free_chlorine", "total_chlorine", "combined_chlorine", "bromine"]
    },
    {
        name: "Équilibre",
        params: ["alkalinity", "calcium_hardness", "cyanuric_acid"]
    },
    {
        name: "Minéraux",
        params: ["copper", "iron", "phosphates", "salt", "tds"]
    },
    {
        name: "Autres",
        params: ["temperature", "ozone", "hydrogen_peroxide", "biguanide", "borate"]
    }
];

export default function SpinTouchSection() {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState([]);
    const [parameters, setParameters] = useState({});
    const [analyses, setAnalyses] = useState([]);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [selectedAnalysis, setSelectedAnalysis] = useState(null);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [expandedGroups, setExpandedGroups] = useState(["Désinfection", "Équilibre"]);

    const [form, setForm] = useState({
        user_id: '',
        pool_type: 'piscine',
        notes: '',
        measurements: {}
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [paramsData, usersData] = await Promise.all([
                apiRequest('/spintouch-manual/parameters'),
                apiRequest('/spintouch-manual/users-with-profiles').catch(() => ({ all_users: [] }))
            ]);
            setParameters(paramsData);
            setUsers(usersData.all_users || []);
        } catch (error) {
            console.error('SpinTouch load error:', error);
            // Don't show error toast, just log it
        } finally {
            setLoading(false);
        }
    };

    const loadUserAnalyses = async (userId) => {
        try {
            const data = await apiRequest(`/spintouch-manual/user/${userId}/analyses`);
            setAnalyses(data);
        } catch (error) {
            setAnalyses([]);
        }
    };

    const handleUserChange = (userId) => {
        setForm(prev => ({ ...prev, user_id: userId }));
        if (userId) {
            loadUserAnalyses(userId);
        } else {
            setAnalyses([]);
        }
    };

    const handleMeasurementChange = (param, value) => {
        setForm(prev => ({
            ...prev,
            measurements: {
                ...prev.measurements,
                [param]: value === '' ? null : parseFloat(value)
            }
        }));
    };

    const toggleGroup = (groupName) => {
        setExpandedGroups(prev => 
            prev.includes(groupName)
                ? prev.filter(g => g !== groupName)
                : [...prev, groupName]
        );
    };

    const handleSubmit = async () => {
        if (!form.user_id) {
            toast.error('Veuillez sélectionner un client');
            return;
        }

        // Check if at least one measurement is provided
        const hasValues = Object.values(form.measurements).some(v => v !== null && v !== undefined);
        if (!hasValues) {
            toast.error('Veuillez entrer au moins une mesure');
            return;
        }

        setSaving(true);
        try {
            const result = await apiRequest('/spintouch-manual/analyze', {
                method: 'POST',
                body: JSON.stringify(form)
            });
            
            toast.success('Analyse créée avec succès !');
            setDialogOpen(false);
            setForm({
                user_id: form.user_id,
                pool_type: 'piscine',
                notes: '',
                measurements: {}
            });
            
            // Show the result
            setSelectedAnalysis(result);
            setViewDialogOpen(true);
            
            // Reload analyses for this user
            loadUserAnalyses(form.user_id);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleViewAnalysis = (analysis) => {
        setSelectedAnalysis(analysis);
        setViewDialogOpen(true);
    };

    const handleDeleteAnalysis = async (analysisId) => {
        if (!confirm('Supprimer cette analyse ?')) return;
        
        try {
            await apiRequest(`/spintouch-manual/analysis/${analysisId}`, {
                method: 'DELETE'
            });
            toast.success('Analyse supprimée');
            if (form.user_id) {
                loadUserAnalyses(form.user_id);
            }
        } catch (error) {
            toast.error(error.message);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'excellent':
            case 'good':
                return 'text-green-500 bg-green-500/10';
            case 'warning':
                return 'text-yellow-500 bg-yellow-500/10';
            case 'critical':
                return 'text-red-500 bg-red-500/10';
            default:
                return 'text-muted-foreground bg-muted';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'excellent':
            case 'good':
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case 'warning':
                return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
            case 'critical':
                return <XCircle className="h-4 w-4 text-red-500" />;
            default:
                return null;
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const filteredUsers = users.filter(u => 
        u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (loading) {
        return (
            <Card className="glass rounded-2xl">
                <CardContent className="flex items-center justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </CardContent>
            </Card>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h2 className="font-heading text-2xl font-bold flex items-center gap-2">
                        <Beaker className="h-6 w-6 text-primary" />
                        Analyses SpinTouch
                    </h2>
                    <p className="text-muted-foreground">
                        Saisissez les résultats SpinTouch pour vos clients
                    </p>
                </div>

                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="rounded-full">
                            <Plus className="h-4 w-4 mr-2" />
                            Nouvelle analyse
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Beaker className="h-5 w-5 text-primary" />
                                Saisie des résultats SpinTouch
                            </DialogTitle>
                        </DialogHeader>

                        <div className="space-y-6 py-4">
                            {/* Client Selection */}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label>Client *</Label>
                                    <Select
                                        value={form.user_id}
                                        onValueChange={handleUserChange}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Sélectionner un client" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <div className="p-2">
                                                <Input
                                                    placeholder="Rechercher..."
                                                    value={searchQuery}
                                                    onChange={(e) => setSearchQuery(e.target.value)}
                                                    className="mb-2"
                                                />
                                            </div>
                                            {filteredUsers.length === 0 ? (
                                                <div className="p-4 text-center text-muted-foreground">
                                                    <User className="h-8 w-8 mx-auto mb-2 opacity-50" />
                                                    <p>Aucun client inscrit</p>
                                                    <p className="text-xs">Les clients apparaîtront ici après leur inscription</p>
                                                </div>
                                            ) : (
                                                filteredUsers.map(user => (
                                                    <SelectItem key={user.user_id} value={user.user_id}>
                                                        <div className="flex items-center gap-2">
                                                            <User className="h-4 w-4" />
                                                            <span>{user.name || user.email}</span>
                                                            {user.pool_profile?.volume && (
                                                                <Badge variant="outline" className="ml-2">
                                                                    {user.pool_profile.volume}m³
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </SelectItem>
                                                ))
                                            )}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label>Type de bassin</Label>
                                    <Select
                                        value={form.pool_type}
                                        onValueChange={(v) => setForm(prev => ({ ...prev, pool_type: v }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="piscine">Piscine</SelectItem>
                                            <SelectItem value="spa">Spa / Jacuzzi</SelectItem>
                                            <SelectItem value="piscine_interieure">Piscine intérieure</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {/* Parameters Groups */}
                            <div className="space-y-4">
                                <Label className="text-lg font-semibold">Mesures SpinTouch</Label>
                                <p className="text-sm text-muted-foreground">
                                    Entrez uniquement les paramètres que vous avez mesurés
                                </p>

                                {PARAMETER_GROUPS.map(group => (
                                    <Card key={group.name} className="glass rounded-xl overflow-hidden">
                                        <button
                                            onClick={() => toggleGroup(group.name)}
                                            className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                                        >
                                            <span className="font-semibold">{group.name}</span>
                                            {expandedGroups.includes(group.name) ? (
                                                <ChevronUp className="h-4 w-4" />
                                            ) : (
                                                <ChevronDown className="h-4 w-4" />
                                            )}
                                        </button>
                                        
                                        {expandedGroups.includes(group.name) && (
                                            <CardContent className="pt-0 pb-4">
                                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                                                    {group.params.map(paramKey => {
                                                        const param = parameters[paramKey];
                                                        if (!param) return null;
                                                        return (
                                                            <div key={paramKey} className="space-y-1">
                                                                <Label className="text-sm">
                                                                    {param.name}
                                                                    {param.unit && (
                                                                        <span className="text-muted-foreground ml-1">
                                                                            ({param.unit})
                                                                        </span>
                                                                    )}
                                                                </Label>
                                                                <Input
                                                                    type="number"
                                                                    step="0.1"
                                                                    min={param.min}
                                                                    max={param.max}
                                                                    placeholder={`${param.ideal_min} - ${param.ideal_max}`}
                                                                    value={form.measurements[paramKey] ?? ''}
                                                                    onChange={(e) => handleMeasurementChange(paramKey, e.target.value)}
                                                                    className="h-9"
                                                                />
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </CardContent>
                                        )}
                                    </Card>
                                ))}
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label>Notes (optionnel)</Label>
                                <Textarea
                                    value={form.notes}
                                    onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Observations, contexte, problèmes signalés par le client..."
                                    rows={3}
                                />
                            </div>

                            {/* Submit */}
                            <div className="flex justify-end gap-2 pt-4 border-t">
                                <Button variant="outline" onClick={() => setDialogOpen(false)}>
                                    Annuler
                                </Button>
                                <Button onClick={handleSubmit} disabled={saving}>
                                    {saving ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Analyse en cours...
                                        </>
                                    ) : (
                                        <>
                                            <Beaker className="h-4 w-4 mr-2" />
                                            Analyser
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Client Selection for History */}
            <Card className="glass rounded-2xl">
                <CardHeader>
                    <CardTitle>Historique des analyses</CardTitle>
                    <CardDescription>Sélectionnez un client pour voir ses analyses</CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="flex gap-4 items-end">
                        <div className="flex-1">
                            <Label>Client</Label>
                            <Select
                                value={form.user_id}
                                onValueChange={handleUserChange}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Sélectionner un client" />
                                </SelectTrigger>
                                <SelectContent>
                                    {users.map(user => (
                                        <SelectItem key={user.user_id} value={user.user_id}>
                                            {user.name || user.email}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>

                    {/* Analyses List */}
                    {form.user_id && (
                        <div className="mt-6 space-y-3">
                            {analyses.length === 0 ? (
                                <p className="text-muted-foreground text-center py-8">
                                    Aucune analyse pour ce client
                                </p>
                            ) : (
                                analyses.map(analysis => (
                                    <div 
                                        key={analysis.analysis_id}
                                        className="flex items-center gap-4 p-4 bg-muted/50 rounded-xl"
                                    >
                                        <div className={`p-2 rounded-full ${getStatusColor(analysis.expert_analysis?.overall_status)}`}>
                                            {getStatusIcon(analysis.expert_analysis?.overall_status) || <Beaker className="h-4 w-4" />}
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <span className="font-semibold">
                                                    Analyse du {formatDate(analysis.created_at)}
                                                </span>
                                                <Badge variant="outline">
                                                    {analysis.pool_type}
                                                </Badge>
                                            </div>
                                            <p className="text-sm text-muted-foreground line-clamp-1">
                                                {analysis.expert_analysis?.summary || 'Analyse complète'}
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleViewAnalysis(analysis)}
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDeleteAnalysis(analysis.analysis_id)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* View Analysis Dialog */}
            <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <Beaker className="h-5 w-5 text-primary" />
                            Résultat de l'analyse
                        </DialogTitle>
                    </DialogHeader>

                    {selectedAnalysis && (
                        <div className="space-y-6 py-4">
                            {/* Overall Status */}
                            {selectedAnalysis.expert_analysis && (
                                <>
                                    <div className={`p-4 rounded-xl ${getStatusColor(selectedAnalysis.expert_analysis.overall_status)}`}>
                                        <div className="flex items-center gap-3">
                                            {getStatusIcon(selectedAnalysis.expert_analysis.overall_status)}
                                            <div>
                                                <p className="font-semibold text-lg">
                                                    {selectedAnalysis.expert_analysis.overall_status === 'excellent' && "Excellent"}
                                                    {selectedAnalysis.expert_analysis.overall_status === 'good' && "Bon état"}
                                                    {selectedAnalysis.expert_analysis.overall_status === 'warning' && "Attention requise"}
                                                    {selectedAnalysis.expert_analysis.overall_status === 'critical' && "Intervention urgente"}
                                                </p>
                                                <p className="text-sm opacity-80">
                                                    {selectedAnalysis.expert_analysis.summary}
                                                </p>
                                            </div>
                                            {selectedAnalysis.expert_analysis.overall_score && (
                                                <Badge className="ml-auto text-lg">
                                                    {selectedAnalysis.expert_analysis.overall_score}/100
                                                </Badge>
                                            )}
                                        </div>
                                    </div>

                                    {/* Parameters */}
                                    {selectedAnalysis.expert_analysis.parameters_analysis && (
                                        <div>
                                            <h3 className="font-semibold mb-3">Paramètres analysés</h3>
                                            <div className="grid gap-2">
                                                {selectedAnalysis.expert_analysis.parameters_analysis.map((param, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg"
                                                    >
                                                        {getStatusIcon(param.status)}
                                                        <div className="flex-1">
                                                            <span className="font-medium">{param.name}</span>
                                                            <span className="text-muted-foreground ml-2">
                                                                {param.value} {param.unit}
                                                            </span>
                                                        </div>
                                                        <span className="text-sm text-muted-foreground">
                                                            Idéal: {param.ideal_range}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Issues */}
                                    {selectedAnalysis.expert_analysis.issues?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 text-red-500">Problèmes identifiés</h3>
                                            <div className="space-y-2">
                                                {selectedAnalysis.expert_analysis.issues.map((issue, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="p-3 bg-red-500/10 rounded-lg border border-red-500/20"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Badge variant={issue.severity === 'critical' ? 'destructive' : 'outline'}>
                                                                {issue.severity}
                                                            </Badge>
                                                            <span className="font-medium">{issue.issue}</span>
                                                        </div>
                                                        {issue.consequence && (
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                {issue.consequence}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Recommendations */}
                                    {selectedAnalysis.expert_analysis.recommendations?.length > 0 && (
                                        <div>
                                            <h3 className="font-semibold mb-3 text-primary">Recommandations</h3>
                                            <div className="space-y-2">
                                                {selectedAnalysis.expert_analysis.recommendations.map((rec, idx) => (
                                                    <div 
                                                        key={idx}
                                                        className="p-3 bg-primary/5 rounded-lg border border-primary/20"
                                                    >
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <Badge>{rec.priority}</Badge>
                                                            <span className="font-medium">{rec.action}</span>
                                                        </div>
                                                        {rec.product && (
                                                            <p className="text-sm">
                                                                <strong>Produit:</strong> {rec.product}
                                                            </p>
                                                        )}
                                                        {rec.dosage && (
                                                            <p className="text-sm">
                                                                <strong>Dosage:</strong> {rec.dosage}
                                                            </p>
                                                        )}
                                                        {rec.details && (
                                                            <p className="text-sm text-muted-foreground mt-1">
                                                                {rec.details}
                                                            </p>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Next Test */}
                                    {selectedAnalysis.expert_analysis.next_test && (
                                        <div className="p-3 bg-muted/50 rounded-lg">
                                            <p className="text-sm">
                                                <strong>Prochain test recommandé:</strong> {selectedAnalysis.expert_analysis.next_test}
                                            </p>
                                        </div>
                                    )}
                                </>
                            )}

                            {/* Raw Measurements */}
                            <div>
                                <h3 className="font-semibold mb-3">Mesures brutes</h3>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                    {Object.entries(selectedAnalysis.measurements || {}).map(([key, value]) => {
                                        if (value === null || value === undefined) return null;
                                        const param = parameters[key];
                                        return (
                                            <div key={key} className="p-2 bg-muted/50 rounded">
                                                <span className="text-muted-foreground">{param?.name || key}:</span>
                                                <span className="ml-1 font-medium">{value} {param?.unit}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
